import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
  Badge,
} from '@rnd-payplus/alloy-react';
import { ExternalLink } from 'lucide-react';
import { usePageShell } from '../contexts/PageShellContext';

const steps = [
  { step: '1', label: 'Create a Tenant', detail: 'Go to Tenants and create a new tenant. A tenant represents an organisation or business unit.' },
  { step: '2', label: 'Add an Integration', detail: 'Go to Integrations and add a new integration. Select your source platform (SAP, Oracle, etc.) and enter credentials.' },
  { step: '3', label: 'Define a Mapping', detail: 'Go to Data Mapping and create a mapping. Map source fields to target fields. Use AI Auto-Generate for a head start.' },
  { step: '4', label: 'Create a Data Flow', detail: 'Go to Data Flows and build a flow. Link source, transforms, and destination nodes visually.' },
  { step: '5', label: 'Test & Monitor', detail: 'Test your flow with sample data from the canvas. Monitor live activity in Analytics and Observability.' },
];

const faqSections = [
  {
    section: 'Data Mappings',
    questions: [
      { q: 'What is the AI Auto-Generate feature?', a: 'Paste sample JSON from your source and target systems. Claude AI analyses both schemas and suggests field mappings with confidence scores (High/Medium/Low). You can edit or remove any mapping before saving.' },
      { q: 'What formats does Auto-Detect support?', a: 'Auto-Detect analyses your saved mapping history and uses semantic aliases (e.g. documentNumber to paymentReference) and string similarity to suggest mappings for the current source/target pair.' },
    ],
  },
  {
    section: 'SAP Payments',
    questions: [
      { q: 'What file formats does the SAP upload accept?', a: 'The SAP upload accepts F110 XML files (containing IDOC elements) and SAP IDoc flat text files (with E1PYCRL/E1PYORD segments). Maximum file size: 10 MB.' },
      { q: 'Where do uploaded payment runs go?', a: 'Processed records are stored in the sap_payments table and appear on the SAP Payments page. They are also linked to their tenant for multi-tenant isolation.' },
    ],
  },
  {
    section: 'Workflows',
    questions: [
      { q: 'What step types are available?', a: 'extract, transform, validate, load, notify. Each step type has a corresponding executor that runs in sequence. Step outputs are stored as context data and passed to the next step.' },
      { q: 'Can I pause and resume a running workflow?', a: 'Yes. Click the Pause button on a running instance. The workflow stops at the current step boundary. Click Resume to continue from where it left off.' },
    ],
  },
  {
    section: 'Invoice OCR',
    questions: [
      { q: 'What file types does Invoice OCR support?', a: 'JPEG, PNG, WEBP, GIF images. PDF files are accepted but processed with lower accuracy. For best results, use high-resolution JPEG or PNG scans.' },
      { q: 'What does the confidence score mean?', a: 'Confidence 80-100: High (all key fields extracted reliably). 50-79: Medium (some fields may need review). Below 50: Low (manual verification recommended).' },
    ],
  },
  {
    section: 'API Keys & Webhooks',
    questions: [
      { q: 'Can I see my full API key after generation?', a: 'No. The full key is shown once at generation time. Only the 8-character prefix is stored and displayed afterward. Copy and store your key securely at generation time.' },
      { q: 'How do I use an API key?', a: 'Include it as a Bearer token: Authorization: Bearer chk_xxxx. The key grants access scoped to its assigned tenant.' },
      { q: 'How many retries does webhook delivery attempt?', a: 'Three attempts with exponential backoff: immediate, then after 1 second, then after 5 seconds, then after 25 seconds. Delivery status is logged to the Webhook Deliveries history.' },
    ],
  },
];

const HelpPage = () => {
  usePageShell({
    title: 'Help & Documentation',
    breadcrumbs: [
      { label: 'Home', href: '/dashboard' },
      { label: 'Help' },
    ],
  });

  return (
    <div className="space-y-5">
      {/* Two-column: Getting Started + Support */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Getting Started */}
        <div>
          <h2 className="text-base font-medium leading-6 mb-4">Getting Started</h2>
          <ol className="space-y-4">
            {steps.map(({ step, label, detail }) => (
              <li key={step} className="flex items-start gap-3">
                <Badge className="mt-0.5 min-w-7 justify-center shrink-0">{step}</Badge>
                <div>
                  <p className="text-sm font-medium">{label}</p>
                  <p className="text-sm text-muted-foreground">{detail}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>

        {/* Support & Resources */}
        <div>
          <h2 className="text-base font-medium leading-6 mb-4">Support & Resources</h2>
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium mb-1">Technical Support</p>
              <p className="text-sm text-muted-foreground">
                Email{' '}
                <a href="mailto:support@connectivity-hub.io" className="text-primary underline">
                  support@connectivity-hub.io
                </a>
              </p>
            </div>
            <div>
              <p className="text-sm font-medium mb-1">Response SLA</p>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">P1 (service down): 4 hours</p>
                <p className="text-sm text-muted-foreground">P2 (degraded): 24 hours</p>
                <p className="text-sm text-muted-foreground">P3 (general): 72 hours</p>
              </div>
            </div>
            <div>
              <p className="text-sm font-medium mb-1">API Reference</p>
              <p className="text-sm text-muted-foreground">
                Available at <code className="text-xs bg-muted px-1.5 py-0.5 rounded">/api-docs</code> via the gateway Swagger UI.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* FAQs */}
      <div>
        <h2 className="text-base font-medium leading-6 mb-4">Frequently Asked Questions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {faqSections.map(({ section, questions }) => (
            <div key={section}>
              <p className="text-sm font-semibold mb-2">{section}</p>
              <Accordion type="multiple" className="space-y-1">
                {questions.map(({ q, a }) => (
                  <AccordionItem key={q} value={q}>
                    <AccordionTrigger>
                      <span className="text-sm text-left">{q}</span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <p className="text-sm text-muted-foreground">{a}</p>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HelpPage;
